// Reading Basket: a docked, collapsible side panel on desktop and a bottom
// sheet (<dialog>) on mobile, opened from a persistent button.
import { useEffect, useId, useRef, useState } from "react";
import { SHARED_LIST_MAX, sharedListPath } from "../../shared/sharedList.ts";
import { normalize, resumeIndex, totalMinutes } from "../lib/basket.ts";
import { navigate } from "../hooks/useRouter.ts";
import { useIsDesktop } from "../hooks/useMediaQuery.ts";
import { config } from "../lib/firebase.ts";
import { useApp } from "../state/AppState.tsx";
import { FEATURES } from "../lib/features.ts";
import { Chevron, btn } from "./ui.tsx";

function BasketContents({ onClose }: { onClose(): void }) {
  const app = useApp();
  const items = normalize(app.basket);
  const minutesById = new Map([...app.stories.values()].map((s) => [s.id, s.estimatedReadMinutes]));
  const total = totalMinutes(items, minutesById);
  const unread = totalMinutes(items, minutesById, true);
  const anyProgress = items.some((b) => b.isRead || (b.readingProgress ?? 0) > 0);
  const allRead = items.length > 0 && items.every((b) => b.isRead);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <h2 className="font-display text-xl font-semibold">Reading Basket</h2>
          <p className="text-sm text-ink-soft" aria-live="polite">
            {items.length} saved · ~{total} min <span className="italic">(estimate)</span>
            {items.length > 0 && unread !== total ? ` · ~${unread} min unread` : ""}
          </p>
        </div>
        <button type="button" onClick={onClose} className={btn.ghost} aria-label="Close Reading Basket">
          Close
        </button>
      </div>

      <p className="mx-5 mt-3 rounded-xl bg-sand px-3 py-2 text-xs leading-relaxed text-ink">
        {app.user
          ? app.syncState === "error"
            ? "Sync problem: recent changes may not be saved to your account."
            : app.syncState === "syncing"
              ? "Syncing with your account…"
              : "Synced to your account."
          : app.authAvailable && FEATURES.signIn
            ? "Guest mode: saves are stored on this device only. Sign in with Google to keep them across devices."
            : "Saves are stored in this browser on this device only."}
      </p>

      {items.length === 0 ? (
        <div className="grid flex-1 place-items-center px-6 py-10 text-center text-sm text-ink-soft">
          <p>
            Nothing saved yet. Choose <strong className="text-ink">Read later</strong> on any update to build your basket.
          </p>
        </div>
      ) : (
        <ol className="flex-1 space-y-2 overflow-y-auto px-5 py-3" aria-label="Saved stories in reading order">
          {items.map((item, i) => {
            const s = app.stories.get(item.storyId);
            const gone = app.unavailableIds.has(item.storyId);
            const title = s?.title ?? (gone ? "No longer available" : "Loading…");
            return (
              <li key={item.storyId} className={`rounded-xl border border-line bg-card p-3 ${item.isRead ? "opacity-75" : ""}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 shrink-0 text-right text-sm font-semibold tabular-nums text-ink-soft" aria-hidden="true">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold leading-snug ${item.isRead ? "line-through decoration-ink/40" : ""}`}>{title}</p>
                    <p className="mt-0.5 text-xs text-ink-soft">
                      {s ? `~${s.estimatedReadMinutes} min (est.)` : gone ? "Removed or unpublished by the editors" : ""}
                      {!item.isRead && (item.readingProgress ?? 0) > 0 ? " · in progress" : ""}
                      {s?.isSample ? " · sample" : ""}
                    </p>
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-xs font-medium">
                      <input type="checkbox" className="size-4 accent-coral-deep" checked={item.isRead} onChange={(e) => app.setRead(item.storyId, e.target.checked)} />
                      Mark read<span className="sr-only">: {title}</span>
                    </label>
                  </div>
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <button type="button" className={`${btn.icon} size-8`} onClick={() => app.move(item.storyId, -1)} disabled={i === 0} aria-label={`Move “${title}” up`}>
                      <Chevron dir="up" />
                    </button>
                    <button type="button" className={`${btn.icon} size-8`} onClick={() => app.move(item.storyId, 1)} disabled={i === items.length - 1} aria-label={`Move “${title}” down`}>
                      <Chevron dir="down" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <button type="button" className="text-xs font-semibold text-ink-soft underline decoration-line-strong underline-offset-[3px] hover:text-ink" onClick={() => app.remove(item.storyId)} aria-label={`Remove “${title}” from basket`}>
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className="grid gap-2 border-t border-line px-5 py-4">
        <button type="button" className={btn.primary} disabled={!items.length} onClick={() => go("/read")}>
          {allRead ? "Read again" : anyProgress ? `Resume reading (story ${resumeIndex(items) + 1})` : "Start reading session"}
        </button>
        {items.length > 0 && <ShareList ids={items.map((b) => b.storyId).filter((id) => !app.unavailableIds.has(id))} />}
        {FEATURES.briefing && (
          <button type="button" className={btn.secondary} disabled={!items.length} onClick={() => go("/briefing")}>
            Build my briefing
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Share the basket as a link to a read-only list (/shared?ids=…). The link
 * carries the stories and order; nothing is uploaded or stored.
 */
function ShareList({ ids }: { ids: string[] }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const nameId = useId();
  const shareIds = ids.slice(0, SHARED_LIST_MAX);
  const url = `${window.location.origin}${sharedListPath(shareIds, name)}`;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setMsg("Link copied.");
    } catch {
      setMsg("Couldn't copy. Your browser blocked clipboard access.");
    }
    window.setTimeout(() => setMsg(""), 4000);
  };

  return (
    <div className="rounded-xl border border-line bg-card">
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={`${btn.ghost} w-full justify-between text-ink`}>
        <span className="inline-flex items-center gap-2 font-semibold">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7.5 7.5 12 3l4.5 4.5M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6" /></svg>
          Share this list
        </span>
        <Chevron dir={open ? "up" : "down"} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-line p-3">
          {shareIds.length ? (
            <>
              <label htmlFor={nameId} className="block text-xs font-semibold text-ink">Title (optional)</label>
              <input
                id={nameId}
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Lakeflow updates for the team"
                className="min-h-11 w-full rounded-lg border border-line-strong bg-card px-3 text-sm"
              />
              <button type="button" className={`${btn.secondary} w-full`} onClick={() => void copy()}>Copy link</button>
              <p className="text-xs leading-snug text-ink-soft" aria-live="polite">
                {msg || (
                  <>
                    Anyone with the link sees these {shareIds.length} updates in this order and can save them to their own basket.
                    {ids.length > SHARED_LIST_MAX && ` Only the first ${SHARED_LIST_MAX} are included.`}
                    {local && <strong className="mt-1 block text-coral-deep">The link points to localhost, so it works for others only after the app is deployed.</strong>}
                  </>
                )}
              </p>
            </>
          ) : (
            <p className="text-xs leading-snug text-ink-soft">None of the updates in your basket are available any more, so there's nothing to share. Remove them and save new ones.</p>
          )}
        </div>
      )}
    </div>
  );
}

export function BasketPanel() {
  const app = useApp();
  const isDesktop = useIsDesktop();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const close = () => app.setBasketOpen(false);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d || isDesktop) return;
    if (app.basketOpen && !d.open) d.showModal();
    if (!app.basketOpen && d.open) d.close();
  }, [app.basketOpen, isDesktop]);

  if (isDesktop) {
    return (
      <aside
        id="reading-basket"
        aria-label="Reading Basket"
        hidden={!app.basketOpen}
        onKeyDown={(e) => e.key === "Escape" && close()}
        className={`fixed bottom-0 right-0 ${config.mode === "production" ? "top-14" : "top-[81px]"} z-20 w-[23rem] border-l border-line bg-paper shadow-[-12px_0_32px_-24px_rgb(38_38_43/0.35)]`}
      >
        {app.basketOpen && <BasketContents onClose={close} />}
      </aside>
    );
  }

  return (
    <>
      <dialog
        ref={dialogRef}
        id="reading-basket"
        aria-label="Reading Basket"
        onClose={close}
        onClick={(e) => e.target === e.currentTarget && close()}
        className="sheet fixed inset-x-0 bottom-0 top-auto m-0 mt-auto max-h-[85dvh] w-full max-w-none rounded-t-3xl bg-paper p-0 text-ink"
      >
        <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-line-strong" aria-hidden="true" />
        <div className="h-[min(80dvh,40rem)]">{app.basketOpen && <BasketContents onClose={close} />}</div>
      </dialog>
    </>
  );
}
